// Cookieless Matomo analytics (issue #11, PRD §11). The server describes the
// tracker in a `meta[name="matomo"]` tag (ApplicationController#matomo_settings);
// this lib reads it, loads matomo.js and reports pageviews — including Inertia
// client-side navigations, which never re-render the layout. Cookies are
// disabled by explicit instruction (PRD §10), so core analytics needs no
// consent banner under PECR. Without the meta tag nothing loads at all.
import { router } from '@inertiajs/react'

type MatomoSettings = {
  url: string
  site_id: string
  // Matomo custom-dimension id the A/B variant map is reported under
  // (docs/Matomo-Setup.md); null until configured in Matomo.
  variant_dimension: string | null
  variants: Record<string, string>
}

declare global {
  interface Window {
    _paq?: unknown[][]
  }
}

let lastTrackedUrl: string | null = null

function trackPageView(url: string): void {
  // Inertia fires `navigate` on the initial load in some flows and not in
  // others; deduping on URL means first paint is tracked exactly once.
  if (url === lastTrackedUrl) return
  lastTrackedUrl = url

  window._paq?.push(['setCustomUrl', url])
  window._paq?.push(['setDocumentTitle', document.title])
  window._paq?.push(['trackPageView'])
}

// One-off events outside the pageview stream — a 404 miss, a click-to-load.
// Safe no-op when Matomo isn't configured (window._paq never exists) and
// during SSR.
export function trackEvent(category: string, action: string, name?: string): void {
  if (typeof window === 'undefined') return
  window._paq?.push(name === undefined ? ['trackEvent', category, action] : ['trackEvent', category, action, name])
}

// Matomo's first-class site-search report (issue #17: the 404 page's search).
// Same no-op guarantees as trackEvent.
export function trackSiteSearch(query: string, category: string, resultCount: number): void {
  if (typeof window === 'undefined') return
  window._paq?.push(['trackSiteSearch', query, category, resultCount])
}

export function initMatomo(): void {
  if (typeof window === 'undefined') return

  const meta = document.querySelector<HTMLMetaElement>('meta[name="matomo"]')
  if (!meta?.content) return

  let settings: MatomoSettings
  try {
    settings = JSON.parse(meta.content) as MatomoSettings
  } catch {
    return // a malformed tag disables analytics, never the page
  }

  const _paq = (window._paq = window._paq ?? [])
  _paq.push(['disableCookies']) // cookieless: the whole no-banner premise
  _paq.push(['setTrackerUrl', `${settings.url}/matomo.php`])
  _paq.push(['setSiteId', settings.site_id])

  const variants = Object.entries(settings.variants)
  if (settings.variant_dimension && variants.length > 0) {
    _paq.push([
      'setCustomDimension',
      Number(settings.variant_dimension),
      variants.map(([key, variant]) => `${key}:${variant}`).join(';'),
    ])
  }

  trackPageView(window.location.pathname + window.location.search)
  // After the page view, per Matomo's own ordering — and AGAIN after every
  // Inertia navigation (Build-Update 2026-10-12 §4.2). enableLinkTracking
  // attaches its click handlers by scanning the DOM at the moment it is
  // called, so on an SPA it only ever sees the first page's links: an outbound
  // click on any client-navigated page went untracked. That is the most likely
  // reason Matomo Goal 2 ("Remote support session started", the Splashtop
  // download on /support) has recorded zero conversions since launch — a
  // visitor almost never lands directly on /support. Re-calling is the
  // documented SPA fix and is safe: Matomo marks the elements it has already
  // bound, so handlers are not stacked.
  _paq.push(['enableLinkTracking'])

  router.on('navigate', (event) => {
    trackPageView(event.detail.page.url)
    _paq.push(['enableLinkTracking'])
  })

  const script = document.createElement('script')
  script.async = true
  script.src = `${settings.url}/matomo.js`
  document.head.appendChild(script)
}
